import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import type { Event as SentryEvent } from '@sentry/angular';
import { environment } from 'environments/environment';
import { VERSION } from 'environments/version';

/** Project web-customer-csp, key reserved for framing events so CSP report noise cannot rate-limit them away. */
const FRAMING_REPORT_DSN = 'https://4a2a2bf383064a488d13d44ee54d9cfc@o4505187202760704.ingest.us.sentry.io/4512333308231680';
const MAX_TAG_LENGTH = 64;

export interface FramingContext {
	/** Origin of the embedding page, or 'unknown'. */
	parentOrigin: string;
	/** Origin of the top-level page when the frame is nested (attacker > partner > us), else same as parentOrigin. */
	topOrigin: string;
	/** Where parentOrigin came from: ancestorOrigins is authoritative, referrer can be a previous document in the frame. */
	originSource: 'ancestorOrigins' | 'referrer' | 'none';
	/** Route shape only: `/p/<partner>` for white-label pages, the first path segment otherwise. Never ids or query strings. */
	routeShape: string;
	partner: string;
}

/** Any origin a URL parser cannot produce a host for ('null' for about:, javascript:, srcdoc) counts as unknown. */
export const originOf = (url: string): string => {
	try {
		const origin = new URL(url).origin;
		return origin && origin !== 'null' ? origin : 'unknown';
	} catch {
		return 'unknown';
	}
};

/** 32 hex chars. `crypto.randomUUID` is missing in insecure contexts and in test runners. */
export const eventId = (): string => {
	const uuid = globalThis.crypto?.randomUUID?.();
	if (uuid) {
		return uuid.replace(/-/g, '');
	}
	return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
};

export const routeShapeOf = (pathname: string): { routeShape: string; partner: string } => {
	const [, first = '', second = ''] = pathname.split('/');
	if (first === 'p' && second) {
		const partner = second.slice(0, MAX_TAG_LENGTH);
		return { routeShape: `/p/${partner}`, partner };
	}
	return { routeShape: `/${first.slice(0, MAX_TAG_LENGTH)}`, partner: 'none' };
};

/**
 * Built by hand and sent with `client.sendEvent` on purpose. Anything that goes through
 * `captureMessage` is merged with the page-wide isolation scope, which carries the main
 * Sentry client's user, tags, breadcrumbs (URLs) and session; none of that may leave for
 * this project.
 */
export const buildFramingEvent = (ctx: FramingContext, isProduction: boolean, now: number = Date.now()): SentryEvent => ({
	event_id: eventId(),
	message: `Framed by ${ctx.parentOrigin}`,
	level: 'info',
	platform: 'javascript',
	timestamp: now / 1000,
	environment: isProduction ? 'production' : 'development',
	release: VERSION,
	// One Sentry issue per embedding origin. Without this Sentry groups every "Framed by …"
	// message into a single issue and the allowlist review (TE-4147) loses the per-partner view.
	fingerprint: ['framed-by', ctx.parentOrigin],
	tags: {
		parent_origin: ctx.parentOrigin,
		top_origin: ctx.topOrigin,
		origin_source: ctx.originSource,
		app: 'web-customer',
		partner: ctx.partner,
		route_shape: ctx.routeShape,
	},
});

export const framingContext = (win: Window = window, doc: Document = document): FramingContext | null => {
	if (win.self === win.top) {
		return null;
	}
	const ancestors = win.location.ancestorOrigins;
	let parentOrigin = 'unknown';
	let topOrigin = 'unknown';
	let originSource: FramingContext['originSource'] = 'none';
	if (ancestors && ancestors.length > 0) {
		parentOrigin = originOf(ancestors[0]);
		topOrigin = originOf(ancestors[ancestors.length - 1]);
		originSource = 'ancestorOrigins';
	} else if (doc.referrer) {
		parentOrigin = originOf(doc.referrer);
		topOrigin = parentOrigin;
		originSource = 'referrer';
	}
	return { parentOrigin, topOrigin, originSource, ...routeShapeOf(win.location.pathname) };
};

/**
 * Reports "who embeds us". The browsers' CSP frame-ancestors reports are not enough: Chromium
 * sends only our own origin, Safari sends nothing, only Firefox names the embedder. So the page
 * reports the parent origin itself, once per load, when it runs inside a frame. A native app webview is a
 * top-level browsing context and never reports. Same-origin framing (our own white-label
 * iframe test page) is skipped: it says nothing about partners.
 */
@Injectable({
	providedIn: 'root',
})
export class FramingReportService {
	init(): void {
		try {
			const ctx = framingContext();
			if (!ctx || (ctx.parentOrigin === window.location.origin && ctx.topOrigin === window.location.origin)) {
				return;
			}
			const client = new Sentry.BrowserClient({
				dsn: FRAMING_REPORT_DSN,
				transport: Sentry.makeFetchTransport,
				stackParser: Sentry.defaultStackParser,
				integrations: [],
				sendDefaultPii: false,
			});
			client.sendEvent(buildFramingEvent(ctx, environment.production));
		} catch {
			// Best effort. A failing reporter must never affect the page inside a partner's frame.
		}
	}
}
